/**
 * Runtime-safe variable references for template mentions.
 *
 * FunnelVariable.id is the canonical storage key, but older auto-created
 * variables may have UUID ids. Those ids are poor editor labels and historically
 * did not parse as @mentions in V2 runtime text. Use a safe display/runtime ref
 * for template insertion while preserving the original variable id elsewhere.
 */

import type { FunnelVariable } from '@segmently/schema-models/v2';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RUNTIME_SAFE_REF_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function isUuidVariableRef(value: string | undefined): boolean {
  return Boolean(value && UUID_PATTERN.test(value));
}

export function isRuntimeSafeVariableRef(value: string | undefined): boolean {
  return Boolean(value && RUNTIME_SAFE_REF_PATTERN.test(value));
}

export function slugifyVariableRef(value: string | undefined): string {
  let slug = (value ?? '')
    .trim()
    .replace(/^@+/, '')
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  if (!slug) {
    slug = 'variable';
  }

  if (/^[0-9]/.test(slug)) {
    slug = `variable_${slug}`;
  }

  return slug;
}

export function getVariableReference(
  variable: Pick<FunnelVariable, 'id' | 'name'>,
): string {
  if (isRuntimeSafeVariableRef(variable.id) && !isUuidVariableRef(variable.id)) {
    return variable.id;
  }

  return slugifyVariableRef(variable.name || variable.id);
}
